import React, { ReactNode, useState, useContext } from 'react';

import { Box, Heading, Link, Text, LayoutContext } from '@lucid/components-react';
import { getI18nData } from '@lucid/utils';
import reactHTMLParser from 'html-react-parser';
import { graphql, useStaticQuery } from 'gatsby';
import { mqMin } from '@lucid/theme';
import { GreenhouseJsonEdge } from 'generated/drupal-graphql';

import type { Lucid } from 'types/types';
import JobPostingStructuredData from 'components/job-posting-structured-data/job-posting-structured-data';

type Props = {
  category: string;
  jobs: GreenhouseJsonEdge[];
  blurb: ReactNode;
};

const CareerCategory = ({ category, jobs, blurb }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openJobs, setOpenJobs] = useState<Record<string, boolean>>({});

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_job_details
              }
            }
          }
        }
      }
    `,
  );

  const {
    language: { i18nKey },
  } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, i18nKey);

  const replaceAll = (string: string, search: string, replace: string) => {
    return string.split(search).join(replace);
  };

  return (
    <Box key={category}>
      <button
        className={isOpen ? 'category-isOpen' : ''}
        css={(theme: Lucid.Theme) => ({
          background: 'transparent',
          border: 0,
          borderBottom: '4px solid transparent',
          textAlign: 'left',
          width: '100%',
          padding: `${theme.space[5]}px ${theme.space[3]}px`,
          [mqMin.tablet]: {
            padding: `${theme.space[7]}px ${theme.space[6]}px`,
          },
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            backgroundColor: theme.colors.coolGrey[10],
          },
          '&:hover, &.category-isOpen': {
            backgroundColor: theme.colors.coolGrey[0],
            borderBottom: `4px solid ${theme.colors.primary}`,
            cursor: 'pointer',
          },
        })}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Heading
          as="h3"
          css={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          m={0}
          variant="headlineM"
        >
          {category}
          <Text m={0} variant="labelS400">
            {jobs.length}
          </Text>
        </Heading>
      </button>
      {isOpen &&
        jobs.map(job => {
          const jobId = job.node.id;
          const jobTitle = job.node.title;
          const jobLocation = job.node.location;
          const jobDescription = job.node.description!;
          const jobUrl = job.node.url;

          return (
            <Box
              key={jobId}
              css={(theme: Lucid.Theme) => ({
                borderBottom: `1px solid ${theme.colors.coolGrey[10]}`,
              })}
              display="flex"
              flexWrap="wrap"
              justifyContent="space-between"
              px={{ default: 3, tablet: 9 }}
              py={{ default: 3, tablet: 7 }}
            >
              <JobPostingStructuredData job={job.node} />
              <Box alignItems="flex-end" display="flex" mb={3}>
                <Text as="span" css={(theme: Lucid.Theme) => ({ margin: theme.space[0] })} variant="paragraphM400">
                  {jobTitle}
                </Text>
                <a
                  css={(theme: Lucid.Theme) => ({
                    marginLeft: theme.space[3],
                  })}
                  href={job.node.url!.replace('/apply', '')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <svg
                    css={theme => ({
                      stroke: theme.colors.coolGrey[70],
                      '&:hover': { stroke: theme.colors.coolGrey[100] },
                    })}
                    fill="none"
                    height="24"
                    viewBox="0 0 24 24"
                    width="24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.3333 11.8889V17.2222C17.3333 17.6937 17.146 18.1459 16.8126 18.4793C16.4792 18.8127 16.0271 19 15.5556 19H5.77778C5.30628 19 4.8541 18.8127 4.5207 18.4793C4.1873 18.1459 4 17.6937 4 17.2222V7.44445C4 6.97295 4.1873 6.52077 4.5207 6.18737C4.8541 5.85397 5.30628 5.66667 5.77778 5.66667H11.1111"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.35"
                    />
                    <path
                      d="M14.6667 3H20.0001V8.33333"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.35"
                    />
                    <path
                      d="M10.2222 12.7778L19.9999 3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.35"
                    />
                  </svg>
                </a>
              </Box>
              <Text as="span" variant="paragraphS400">
                {jobLocation}
              </Text>
              <Box flex="0 1 100%" mt={3}>
                <Link
                  iconAnimation={openJobs[jobId] ? 'contract' : 'expand'}
                  onClick={event => {
                    event.preventDefault();
                    setOpenJobs(openJobs => {
                      const hasNewStatus = !openJobs[jobId];
                      return {
                        ...openJobs,
                        [jobId]: hasNewStatus,
                      };
                    });
                  }}
                >
                  {i18n('mktg-job-details')}
                </Link>
                {openJobs[jobId] && (
                  <Box
                    css={(theme: Lucid.Theme) => ({
                      '.section': {
                        marginBottom: theme.space[4],
                      },
                    })}
                    maxWidth="580px"
                    mt={6}
                  >
                    {reactHTMLParser(replaceAll(jobDescription!, '<p>&nbsp;</p>', ''))}
                    <Text variant="labelXS400">{blurb}</Text>
                    <Box
                      css={(theme: Lucid.Theme) => ({
                        marginTop: theme.space.s48,
                      })}
                    >
                      <Link displayAsButton newTab to={jobUrl!}>
                        {/* ToDo: i18n */}
                        Apply
                      </Link>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
    </Box>
  );
};

export default CareerCategory;
